// Port of SuiteModel/CommentCategories.swift: a named kind of margin comment with a colour of its own ("Fact-check" in
// red, "Tidy prose" in yellow). The colour belongs to the name, so a comment is given a category, never a bare colour.
// Kept in the project settings, one JSON entry per category at the key `commentCategory.<id>` — in the settings map
// itself, not a map of their own, which two devices each adding their first category would both make, Automerge showing
// only one. A comment names its category by id (`Comment.category`), so renaming or recolouring one changes every
// comment in it. Colours are the writer's inks.
import type { Comment, InkColor } from '../contract.ts';
import type { ProjectRecord } from '../model/documents.ts';
import { stableUUID } from '../hashing.ts';
import { INK_COLORS } from '../proseMarkdown.ts';
import { caseInsensitiveOrder } from '../story/glossary.ts';

export interface CommentCategory {
  id: string;
  name: string;
  /** The ink's raw value. One this build doesn't know (an ink added later) is kept as stored and drawn grey. */
  color: string;
}

export const COMMENT_CATEGORY_PREFIX = 'commentCategory.';
export const COMMENT_CATEGORY_NAME_LIMIT = 40;

/** `CommentCategory.normalized`: trimmed, inner runs of whitespace made one space. */
export const normalizedCategoryName = (name: string) => name.split(/\p{White_Space}+/u).filter((w) => w !== '').join(' ');

/** `CommentCategory.id(for:)`: a category made under a name has the same id on every device. Renaming keeps the id. */
export const commentCategoryID = (name: string) => stableUUID('comment-category:' + normalizedCategoryName(name).toLowerCase());

/** `CommentCategory.key`: what two spellings of one name share ("Fact-check", "fact-check "). */
export const commentCategoryKey = (name: string) => normalizedCategoryName(name).normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

/** `CommentCategory(name:ink:id:)`. */
export function commentCategory(name: string, ink: InkColor, id?: string): CommentCategory {
  const trimmed = normalizedCategoryName(name);
  return { id: id ?? commentCategoryID(trimmed), name: trimmed, color: ink };
}

/** `CommentCategory.ink`. */
export const categoryInk = (c: Pick<CommentCategory, 'color'>): InkColor =>
  (INK_COLORS as readonly string[]).includes(c.color) ? (c.color as InkColor) : 'grey';

/** `CommentCategory(from:)`, the synthesised decoder: throws unless id, name and colour are all text. */
export function decodeCommentCategory(json: unknown): CommentCategory {
  if (!json || typeof json !== 'object' || Array.isArray(json)) throw new Error('not an object');
  const o = json as Record<string, unknown>;
  if (typeof o.id !== 'string') throw new Error('id');
  if (typeof o.name !== 'string') throw new Error('name');
  if (typeof o.color !== 'string') throw new Error('color');
  return { id: o.id, name: o.name, color: o.color };
}

/** `ProjectDocument.commentCategories()`: every readable category, by name. */
export function readCommentCategories(project: Pick<ProjectRecord, 'settings'>): CommentCategory[] {
  const out: CommentCategory[] = [];
  for (const [key, json] of Object.entries(project.settings)) {
    if (!key.startsWith(COMMENT_CATEGORY_PREFIX) || typeof json !== 'string' || json === '') continue;
    try { out.push(decodeCommentCategory(JSON.parse(json))); } catch { /* unreadable: left out */ }
  }
  return out.sort((a, b) => caseInsensitiveOrder(a.name, b.name));
}

const INK_ORDER: readonly InkColor[] = ['red', 'yellow', 'blue', 'green', 'purple', 'orange', 'pink', 'grey'];

/** `CommentCategory.nextInk(for:)`: the colour to offer for a new category — the first ink no category has, else the
 *  least used. */
export function nextCategoryInk(categories: readonly CommentCategory[]): InkColor {
  const used = new Map<InkColor, number>();
  for (const c of categories) used.set(categoryInk(c), (used.get(categoryInk(c)) ?? 0) + 1);
  let best = INK_ORDER[0];
  for (const ink of INK_ORDER) if ((used.get(ink) ?? 0) < (used.get(best) ?? 0)) best = ink;
  return best;
}

/** `category(of:)`: the category a comment is in. A comment naming one that has since been deleted is in none. */
export function categoryOf(comment: Pick<Comment, 'category'>, categories: readonly CommentCategory[]): CommentCategory | null {
  if (comment.category === null || comment.category === undefined) return null;
  return categories.find((c) => c.id === comment.category) ?? null;
}

/** `named(_:)`: the category called `name`, however it's capitalised or spaced. */
export function categoryNamed(name: string, categories: readonly CommentCategory[]): CommentCategory | null {
  const key = commentCategoryKey(name);
  return categories.find((c) => commentCategoryKey(c.name) === key) ?? null;
}
